import React, { createContext, useContext } from 'react';

interface ImagePriorityContextValue {
    loading: 'eager' | 'lazy';
    fetchPriority: 'high' | 'auto';
}

// Pre-compute static value objects for optimal performance and referential stability
// These are module-level constants, created once and reused across all renders
const LAZY_PRIORITY: ImagePriorityContextValue = {
    loading: 'lazy',
    fetchPriority: 'auto',
};

const EAGER_PRIORITY: ImagePriorityContextValue = {
    loading: 'eager',
    fetchPriority: 'high',
};

// CRITICAL: Provide default value instead of undefined to ensure context works during Gatsby SSR
const ImagePriorityContext = createContext<ImagePriorityContextValue>(LAZY_PRIORITY);

interface ImagePriorityProviderProps {
    children: React.ReactNode;
    isEarlyBlock?: boolean;
}

export const ImagePriorityProvider: React.FC<ImagePriorityProviderProps> = ({ children, isEarlyBlock = false }) => {
    // Hero block (index 0) gets eager/high priority for optimal LCP
    // All other blocks get lazy/auto to avoid bandwidth competition
    // Static objects ensure referential stability and are synchronously available during SSR
    const value = isEarlyBlock ? EAGER_PRIORITY : LAZY_PRIORITY;

    return (
        <ImagePriorityContext.Provider value={value}>
            {children}
        </ImagePriorityContext.Provider>
    );
};

// Context always has a value (either from provider or default), guaranteed to work in SSR
export const useImagePriority = (): ImagePriorityContextValue => useContext(ImagePriorityContext);
